import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import UI components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';

// Import page components
import VideoPlayer from './components/VideoPlayer';
import AdminPanel from './components/AdminPanel';
import DMCAPage from './components/DMCAPage';
import ContactPage from './components/ContactPage';
import CategoryPage from './components/CategoryPage';
import HelpPage from './components/HelpPage';
import ReportPage from './components/ReportPage';
import FeaturedPage from './components/FeaturedPage';
import LatestPage from './components/LatestPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import NativeAd from './components/NativeAd';
import NativeAdSection from './components/NativeAdSection';
import AdZone from './components/AdZone';
import Footer from './components/Footer';
import Pagination from './components/Pagination';
import Header from './components/Header';
import { useTracking } from './hooks/useTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Age Verification Component
const AgeVerification = ({ onVerify }) => {
  const [showModal, setShowModal] = useState(true);

  const handleVerify = async (isOver18) => {
    if (isOver18) {
      try {
        await axios.post(`${API}/age-verify`);
        // Store verification in both localStorage (persistent) and sessionStorage (current session)
        localStorage.setItem('ageVerified', 'true');
        sessionStorage.setItem('ageVerified', 'true');
        setShowModal(false);
        onVerify(true);
        console.log('Age verification completed successfully');
      } catch (error) {
        console.error('Age verification failed:', error);
        // Still proceed even if API call fails
        localStorage.setItem('ageVerified', 'true');
        sessionStorage.setItem('ageVerified', 'true');
        setShowModal(false);
        onVerify(true);
      }
    } else {
      window.location.href = 'https://google.com';
    }
  };

  if (!showModal) return null;

  return (
    <Dialog open={showModal} onOpenChange={() => {}}>
      <DialogContent className="max-w-md mx-auto age-verification-modal border-orange-500/30">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-white">
            Age Verification Required
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 p-6">
          <div className="text-center">
            <div className="text-6xl mb-4 animate-glow">🔞</div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Megavideosee offers unlimited free porn videos featuring the hottest porn stars. 
              Enjoy full-length scenes from the best XXX studios, along with the largest amateur porn community on the web. 
              We update our porn videos daily to ensure you always have access to the highest quality sex movies.
            </p>
            <div className="text-sm text-gray-400 mb-8">
              By clicking "I am 18 or older", you confirm that you are of legal age and agree to view adult content.
            </div>
          </div>
          <div className="flex flex-col space-y-3">
            <Button 
              onClick={() => handleVerify(true)}
              className="w-full btn-primary hover-lift font-semibold py-3"
              data-testid="age-verify-yes-btn"
            >
              I am 18 or older - Enter Site
            </Button>
            <Button 
              onClick={() => handleVerify(false)}
              className="w-full btn-secondary py-3"
              data-testid="age-verify-no-btn"
            >
              I am under 18 - Exit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Header and Navigation components moved to /components/Header.js

// Video Card Component - Exact 300×250 dimensions to match banner
const VideoCard = ({ video, onVideoClick }) => {
  return (
    <div 
      className="relative w-full aspect-[6/5] cursor-pointer group overflow-hidden rounded-lg border border-orange-500/10 bg-gray-800"
      onClick={() => onVideoClick(video.id)}
      data-testid={`video-card-${video.id}`}
    >
      {/* Video Thumbnail - Full card size */}
      <img
        src={video.thumbnail_url || `https://picsum.photos/300/250?random=${video.id}`}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      
      {/* Bottom Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <h3 className="text-white font-semibold text-sm mb-1 line-clamp-2 group-hover:text-orange-400 transition-colors">
          {video.title}
        </h3>
        <div className="flex items-center justify-between text-xs text-gray-300">
          <span>{video.view_count.toLocaleString()} views</span>
          <span>{video.rating} ⭐</span>
        </div>
      </div>
      
      {/* Duration Badge */}
      <div className="absolute top-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white backdrop-blur-sm">
        {video.duration ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}` : '10:30'}
      </div>
      
      {/* Play Button Overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
        <div className="w-16 h-16 bg-orange-600/20 rounded-full flex items-center justify-center animate-bounce backdrop-blur-sm">
          <div className="w-0 h-0 border-l-[20px] border-l-white border-y-[12px] border-y-transparent ml-1" />
        </div>
      </div>
    </div>
  );
};

// Enhanced Banner Ad Component
const BannerAd = ({ placement = "homepage", className = "", enhanced = false }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBannerAds();
  }, [placement]);

  const fetchBannerAds = async () => {
    try {
      const response = await axios.get(`${API}/ads/banner/${placement}`);
      setAds(response.data);
    } catch (error) {
      console.error('Failed to fetch banner ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdClick = async () => {
    if (ads.length === 0) return;
    
    const ad = ads[0];
    try {
      await axios.post(`${API}/ads/${ad.id}/click`);
      if (ad.click_url) {
        window.open(ad.click_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to track ad click:', error);
    }
  };

  if (loading) {
    return (
      <div className={`loading-skeleton rounded-lg ${className}`}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-pulse text-gray-400">Loading ad...</div>
        </div>
      </div>
    );
  }

  if (ads.length === 0) {
    return (
      <div className={`glass-effect rounded-lg flex items-center justify-center border-2 border-dashed border-orange-500/20 ${className}`}>
        <div className="text-center p-8">
          <div className="text-4xl mb-2 text-orange-400">📺</div>
          <div className="text-orange-400 font-medium">Ad Space</div>
          <div className="text-gray-500 text-sm">728 × 90</div>
        </div>
      </div>
    );
  }

  const ad = ads[0];

  return (
    <div className={`glass-effect rounded-lg overflow-hidden ${ad.iframe_code ? '' : 'cursor-pointer group'} ${className} ${enhanced ? 'gradient-border animate-glow' : ''}`} 
         onClick={ad.iframe_code ? undefined : handleAdClick}
         data-testid={`banner-ad-${placement}`}>
      {ad.iframe_code ? (
        <div 
          className="w-full h-full"
          dangerouslySetInnerHTML={{ __html: ad.iframe_code }}
        />
      ) : ad.banner_url ? (
        <div className="relative">
          <img 
            src={ad.banner_url} 
            alt={ad.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {enhanced && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-orange-600/90 text-white text-xs">Sponsored</Badge>
            </div>
          )}
        </div>
      ) : (
        <div className="h-full flex items-center justify-center p-8 text-center">
          <div>
            <div className="text-3xl mb-2 text-orange-400">🎯</div>
            <div className="text-white font-medium">{ad.name}</div>
            <div className="text-orange-400 text-sm mt-1">Premium Advertisement</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Homepage Component
const Homepage = () => {
  const [videos, setVideos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [latestUploads, setLatestUploads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Initialize tracking
  useTracking();

  useEffect(() => {
    fetchCategories();
    fetchFeaturedVideos();
    fetchLatestUploads();
    fetchVideos();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setPage(1);
      fetchVideos();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchFeaturedVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos/featured?limit=8`);
      setFeaturedVideos(response.data);
      console.log("🎬 Featured Videos fetched:", response.data.length, "videos", response.data);
    } catch (error) {
      console.error('Failed to fetch featured videos:', error);
    }
  };

  const fetchLatestUploads = async () => {
    try {
      const response = await axios.get(`${API}/videos/latest-uploads?limit=12`);
      setLatestUploads(response.data);
      console.log("📺 Latest Uploads fetched:", response.data.length, "videos", response.data);
    } catch (error) {
      console.error('Failed to fetch latest uploads:', error);
    }
  };

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100'  // Show more videos on homepage
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category_id', selectedCategory);

      const response = await axios.get(`${API}/videos?${params}`);
      setVideos(response.data.items);
      console.log("🎥 All Videos fetched:", response.data.items.length, "videos", response.data);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (videoId) => {
    window.location.href = `/video/${videoId}`;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderVideoGrid = (videos, startIndex = 0) => {
    const items = [];
    
    // Ensure we have videos to work with
    if (videos.length === 0) {
      return <div className="text-center text-gray-400 py-8">No videos available</div>;
    }
    
    // First row: [Video] [Video] [Video] [Banner]
    const firstRowVideos = videos.slice(0, 3);
    items.push(
      <div key="first-row" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* First 3 video positions */}
        {firstRowVideos.map((video) => (
          <div key={video.id} className="w-full">
            <VideoCard video={video} onVideoClick={handleVideoClick} />
          </div>
        ))}
        {/* 4th position: Homepage Right 300x250 Banner */}
        <div className="w-full">
          <AdZone 
            placementId="homepage_right_300x250" 
            className="w-full aspect-[6/5]"
            fallback={
              <div className="w-full aspect-[6/5] bg-gradient-to-br from-orange-600 to-red-600 rounded-lg flex items-center justify-center text-white font-bold">
                300×250 Banner
              </div>
            }
          />
        </div>
      </div>
    );
    
    // Remaining rows: [Video] [Video] [Video] [Video]
    for (let i = 3; i < videos.length; i += 4) {
      const rowVideos = videos.slice(i, i + 4);
      items.push(
        <div key={`row-${i}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {rowVideos.map((video) => (
            <div key={video.id} className="w-full">
              <VideoCard video={video} onVideoClick={handleVideoClick} />
            </div>
          ))}
        </div>
      );
    }
    
    return items;
  };

  return (
    <div className="min-h-screen" data-testid="homepage">
      <Header
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onCategoryClick={setSelectedCategory}
        categories={categories}
      />

      <main className="container mx-auto px-6 py-8">
        {/* Top Banner Ad Zone - Properly spaced and proportional */}
        <div className="mt-12 mb-12 flex justify-center">
          <div className="w-full max-w-[728px]" style={{aspectRatio: '728/90'}}>
            <AdZone 
              placementId="homepage_top_728x90" 
              className="w-full h-full"
              style={{aspectRatio: '728/90'}}
              fallback={
                <div className="w-full h-full bg-gradient-to-r from-orange-600 to-red-600 rounded-lg flex items-center justify-center" style={{aspectRatio: '728/90'}}>
                  <div className="text-white font-bold">728×90 Premium Ad Space</div>
                </div>
              }
            />
          </div>
        </div>

        {/* Main Video Grid - All Videos */}
        <section className="animate-slideInUp">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold title-primary flex items-center">
              <span className="mr-3 text-4xl">🎬</span>
              {searchTerm ? `Search Results: "${searchTerm}"` : 
               selectedCategory ? `Category: ${categories.find(c => c.id === selectedCategory)?.name}` : 
               'All Videos'}
            </h2>
            <div className="text-orange-400 text-sm">
              {videos.length} videos
            </div>
          </div>

          {loading && videos.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-white text-xl animate-pulse">Loading videos...</div>
            </div>
          ) : (
            <div className="space-y-6">
              {renderVideoGrid(videos)}
            </div>
          )}

          {!loading && videos.length === 0 && (
            <div className="text-center py-20 animate-fadeIn">
              <div className="text-6xl mb-4">🎬</div>
              <div className="text-xl text-gray-400 mb-2">No videos found</div>
              <div className="text-gray-500">Try adjusting your search or browse different categories</div>
            </div>
          )}
        </section>

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {/* Native Ad Section at bottom of every page */}
        <NativeAdSection />
      </main>

      <Footer />
    </div>
  );
};

// Main App Component
function App() {
  const [ageVerified, setAgeVerified] = useState(false);

  useEffect(() => {
    const verified = localStorage.getItem('ageVerified');
    const sessionVerified = sessionStorage.getItem('ageVerified');
    
    // Check both localStorage (persistent) and sessionStorage (current session)
    if (verified === 'true' || sessionVerified === 'true') {
      setAgeVerified(true);
    }
    
    // For debugging - force age verification to show
    console.log('Age verification status:', {
      localStorage: verified,
      sessionStorage: sessionVerified,
      ageVerified: ageVerified
    });
  }, []);

  if (!ageVerified) {
    return <AgeVerification onVerify={setAgeVerified} />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/video/:id" element={<VideoPlayer />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/dmca" element={<DMCAPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/categories" element={<CategoryPage />} />
          <Route path="/categories/:categoryId" element={<CategoryPage />} />
          <Route path="/featured" element={<FeaturedPage />} />
          <Route path="/latest" element={<LatestPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/report" element={<ReportPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;